import { useField } from 'formik';
import React, { ReactElement } from 'react';
import { OptionTypeBase } from 'react-select';
import Select from 'react-select';
import { CommonProps } from 'react-select';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import intersectionWith from 'lodash/intersectionWith';

export interface ReactSelectOption {
  value: string;
  label: string;
  options?: ReactSelectOption[];
}

// Make the name required
type Props = CommonProps<ReactSelectOption> & {
  name: string;
  options: ReactSelectOption[];
  customStyles: any;
  placeholder: string;
  isMulti: boolean;
}

export const DefaultReactSelectStyle = {
  container: (provided: React.CSSProperties, state: any) => ({
    ...provided,
    width: 'calc(100% + 2em)',
    margin: '0 0 15px',
  }),
  control: (provided: React.CSSProperties, state: any) => ({
    ...provided,
    borderColor: '#858b90',
  }),
  input: (provided: React.CSSProperties, state: any) => ({
    ...provided,
  }),
  multiValue: (provided: React.CSSProperties, state: any) => ({
    ...omit(provided, 'padding'),
    background: '#f2f4f7',
    borderRadius: `5px`,
    border: '1px solid #BEBEBE',
    margin: '4px',
  }),
  multiValueLabel: (provided: React.CSSProperties, state: any) => ({
    ...omit(provided, ['paddingLeft']),
    padding: '8px',
    fontSize: '100%',
  }),
  multiValueRemove: (provided: React.CSSProperties, state: any) => ({
    ...omit(provided, ['paddingLeft', 'paddingRight']),
    padding: '8px',
    borderLeft: '1px solid #BEBEBE',
    color: '#787878',
  }),
}

export const FormikReactSelect = (props: Props): ReactElement => {
  const { name, options, customStyles, placeholder, isMulti } = props;
  const [field, , helpers] = useField<string[] | string>(name);

  const value = Array.isArray(field.value) ? findSelectedOptions(field.value, options) : findSelectedOption(field.value, options);

  return (
    <Select
      classNamePrefix="react-select"
      enableReinitialization
      isMulti={isMulti}
      styles={{
        ...DefaultReactSelectStyle,
        ...customStyles
      }}
      options={options}
      name={name}
      value={value}
      onChange={option => {        
        if (isMulti) {
          if (isNil(option)) {
            helpers.setValue([]);
          } else if (Array.isArray(option)) {
            helpers.setValue(option.map(opt => (opt as ReactSelectOption).value));
          }
        } else {
          helpers.setValue((option as ReactSelectOption).value);
        }
      }}
      onBlur={field.onBlur}
      placeholder={placeholder}
    />
  );
}

const emptySelection = null;

const findSelectedOption = (value: string, options: ReactSelectOption[]): ReactSelectOption | null => {
  let selection: ReactSelectOption | null = emptySelection;
  if (isNil(options) || isEmpty(value)) return selection;

  for (let index = 0; index < options.length; index++) {
    const option = options[index];
    if (!isNil(option.options)) {
      const foundOption = option.options.find((opt: OptionTypeBase) => opt.value === value);
      if (!isNil(foundOption)) {
        return foundOption;
      }
    }
    if (option.value === value) {
      return option;
    }
  }
  return selection;
}

const findSelectedOptions = (values: string[], options: ReactSelectOption[]): ReactSelectOption[] | null => {
  let selection: ReactSelectOption[] | null = [];
  if (isNil(options) || isEmpty(values)) return selection;

  for (let index = 0; index < options.length; index++) {
    const option = options[index];
    if (!isNil(option.options)) {
      const foundOptions = intersectionWith(option.options, values, (opt: ReactSelectOption, value: string) => opt.value === value)
      selection = [...selection, ...foundOptions];
    }
    if (values.indexOf(option.value) !== -1) {
      selection.push(option);
    }
  }
  return selection;
}
